import { useMemo } from 'react'

import { useMatches } from 'react-router-dom'
import { z } from 'zod'

import { AnchorComponent, ModeAnchor, ROUTE_MODE } from './modes/index.js'

// `forwardRef(...)` returns an object with `typeof === 'object'`, so plain
// `z.function()` rejects it. Accept any callable or `forwardRef`-style object
// — the route handle is produced by `createRouteModeAnchor`, which already
// type-checks the input.
const anchorComponentSchema = z.custom<AnchorComponent>(
  val => typeof val === 'function' || (typeof val === 'object' && val !== null),
)

const anchorSchema = z.partialRecord(
  z.enum(Object.values(ROUTE_MODE)),
  anchorComponentSchema,
)

const routeModeAnchorSchema = z.object({
  mode: z
    .object({
      anchor: anchorSchema.optional(),
    })
    .optional(),
})

export function useRouteModeAnchor() {
  const matches = useMatches()

  return useMemo(() => {
    return matches.reduce((acc, match) => {
      const result = routeModeAnchorSchema.safeParse(match.handle)
      if (!result.success) {
        return acc
      }

      const anchor = result.data.mode?.anchor ?? {}

      Object.entries(anchor).forEach(([mode, anchor]) => {
        if (anchor) {
          acc[mode] = anchor
        }
      })

      return acc
    }, {} as ModeAnchor)
  }, [matches])
}
